// @system — horizontal scroll container ("rail") for tables, tab strips and thumbnail strips.
// When the content is wider than the viewport the rail:
//   - becomes keyboard reachable (tabindex="0") so the scroll position can be moved without a
//     pointer (WCAG 2.1.1; axe scrollable-region-focusable),
//   - shows a visible hint below it (`[data-scroll-hint]`) so touch users know more content is
//     hidden to the side — the responsive gate (e2e/@system/10-responsive.spec.js) requires it.
// Nothing renders when the content fits, so desktop layouts are unchanged.
import { useEffect, useRef, useState } from 'react'
import { text } from '@/config'
import { cn } from '@/app/lib/@system/utils'

/**
 * @typedef {object} ScrollRailOwnProps
 * @property {import('react').ReactNode} [children]
 * @property {string} [className] - Classes for the scrolling element itself
 * @property {string} [wrapperClassName] - Classes for the outer wrapper (hint sits inside it)
 * @property {keyof JSX.IntrinsicElements} [as="div"] - Element type of the scrolling rail (`pre`, `ul`, …)
 * @property {string} [label] - Accessible name; when set the rail is a labelled region
 * @property {string} [hint] - Hint text (defaults to text.a11y.scrollHint)
 * @typedef {ScrollRailOwnProps & import('react').HTMLAttributes<HTMLElement>} ScrollRailProps
 */

/** @param {ScrollRailProps} props */
export function ScrollRail({ children, className, wrapperClassName, as = 'div', label, hint = text.a11y.scrollHint, ...props }) {
  // Polymorphic tag: every intrinsic element accepts these attributes and a DOM ref, but a union of
  // all tag names is too large for the checker (TS2590), so the tag name is viewed as one such component.
  const Tag = /** @type {import('react').ForwardRefExoticComponent<import('react').HTMLAttributes<HTMLElement> & import('react').RefAttributes<HTMLElement>>} */ (
    /** @type {unknown} */ (as)
  )
  const ref = useRef(/** @type {HTMLElement | null} */ (null))
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 1)
    check()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(check)
    ro.observe(el)
    for (const child of el.children) ro.observe(child)
    return () => ro.disconnect()
  }, [children])

  return (
    // `contain: inline-size` keeps the rail's intrinsic width out of ancestor sizing: a table inside
    // a grid/flex item (min-width:auto) would otherwise widen the column — and the page — instead
    // of scrolling inside the rail.
    <div className={cn('min-w-0 [contain:inline-size]', wrapperClassName)}>
      <Tag
        ref={ref}
        data-scroll-rail=""
        role={label ? 'region' : undefined}
        aria-label={label}
        tabIndex={overflows ? 0 : undefined}
        // `relative`: absolutely positioned descendants (sr-only text, Radix bubble inputs) are
        // contained — and clipped — by the rail instead of landing against the viewport.
        className={cn('relative overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary', className)}
        {...props}
      >
        {children}
      </Tag>
      {overflows && (
        <p data-scroll-hint="" className="mt-1 text-xs text-brand-text-muted">
          {hint} →
        </p>
      )}
    </div>
  )
}
